import React, { useState, useRef, useEffect, useLayoutEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { HexColorPicker, HexAlphaColorPicker } from "react-colorful";
import Icon from './Icon';
import { icons } from '../../config/icons';
import { validationRules } from '../../utils/validation';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const Lbl = ({ req, sfx }) => {
    if (typeof sfx !== 'undefined') return sfx ? <span className="suffix" dangerouslySetInnerHTML={{ __html: sfx }} /> : null;
    return req ? <span className="required">*</span> : null;
};
const getErr = (err, val) => {
    if (err === undefined || err === null || err === false) return { hasErr: false, msg: null };
    const msg = typeof err === 'string' ? err : ((val === '' || val == null) ? 'This field is required' : 'This field is invalid');
    return { hasErr: true, msg };
};

export const FormInput = ({ label, name, type = 'text', value, defaultValue, error, onChange, onBlur, placeholder, required, suffix, iconLeft, iconRight, onIconRightClick, iconRightClass = '', hints, className = '', inputClass = '', inputRef, labelRight, ...p }) => {
    const { hasErr, msg } = getErr(error, value);
    const isDouble = iconRight && hasErr;
    const rightClass = isDouble ? 'right-double' : (iconRight || hasErr ? 'right' : '');

    return (
        <div className={`input-case ${hasErr ? 'invalid' : ''} ${className}`}>
            {label && (
                <p style={labelRight ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' } : undefined}>
                    <span>{label} <Lbl req={required} sfx={suffix} /></span>
                    {labelRight && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{labelRight}</span>}
                </p>
            )}
            <div className={`input-group ${iconLeft ? 'left' : ''} ${rightClass}`}>
                <input ref={inputRef} type={type} name={name} value={value} defaultValue={defaultValue} onChange={onChange} onBlur={onBlur} placeholder={placeholder} className={`${iconLeft ? 'left' : ''} ${rightClass} ${inputClass}`} {...p} required={!!required} />
                {iconLeft && <Icon icon={iconLeft} className="input-icon" />}
                {iconRight && <Icon icon={iconRight} className={`input-icon right ${iconRightClass}`} onClick={onIconRightClick} />}
                {hasErr && <Icon icon={icons.invalid} className={`input-icon right ${isDouble ? 'error-icon' : ''}`} />}
            </div>
            {msg && <em>{msg}</em>}
            {hints && <small>{hints}</small>}
        </div>
    );
};

export const FormNumberInput = ({ label, name, value, defaultValue, error, onChange, onBlur, placeholder, required, suffix, iconLeft, className = '', inputClass = '', inputRef, labelRight, min, max, step, disabled, inputWidth, style, ...p }) => {
    const { hasErr, msg } = getErr(error, value);

    const handleIncrement = () => {
        if (disabled) return;
        const current = parseFloat(value) || 0;
        const s = parseFloat(step) || 1;
        if (onChange) onChange({ target: { name, value: String(current + s) } });
    };

    const handleDecrement = () => {
        if (disabled) return;
        const current = parseFloat(value) || 0;
        const s = parseFloat(step) || 1;
        if (onChange) onChange({ target: { name, value: String(current - s) } });
    };

    return (
        <div className={`input-case ${hasErr ? 'invalid' : ''} ${className}`} style={{ width: inputWidth, ...style }}>
            {label && (
                <p style={labelRight ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' } : undefined}>
                    <span>{label} <Lbl req={required} sfx={suffix} /></span>
                    {labelRight && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{labelRight}</span>}
                </p>
            )}
            <div className={`input-group number-input-group ${iconLeft ? 'left' : ''} ${disabled ? 'disabled' : ''}`}>
                <input ref={inputRef} type="number" name={name} value={value} defaultValue={defaultValue} onChange={onChange} onBlur={onBlur} placeholder={placeholder} className={`${iconLeft ? 'left' : ''} ${inputClass}`} min={min} max={max} step={step} disabled={disabled} {...p} required={!!required} />
                {iconLeft && <Icon icon={iconLeft} className="input-icon" />}
                <div className="number-stepper">
                    <button type="button" disabled={disabled} className='stepper-btn' onClick={handleIncrement}><Icon icon={icons.chevronUp} size={10} /></button>
                    <div className='number-stepper-dvr' />
                    <button type="button" disabled={disabled} className='stepper-btn' onClick={handleDecrement}><Icon icon={icons.chevronDown} size={10} /></button>
                </div>
            </div>
            {msg && <em>{msg}</em>}
        </div>
    );
};

export const FormSelect = ({ label, name, value, defaultValue, error, onChange, onBlur, options = [], required, suffix, placeholder = 'Select', className = '', inputClass = '', inputRef, labelRight, ...p }) => {
    const { hasErr, msg } = getErr(error, value);
    return (
        <div className={`input-case ${hasErr ? 'invalid' : ''} ${className}`}>
            {label && (
                <p style={labelRight ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' } : undefined}>
                    <span>{label} <Lbl req={required} sfx={suffix} /></span>
                    {labelRight && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{labelRight}</span>}
                </p>
            )}
            <select ref={inputRef} name={name} value={value} defaultValue={defaultValue} onChange={onChange} onBlur={onBlur} className={inputClass} {...p} required={!!required}>
                <option value="">{placeholder}</option>
                {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            {msg && <em>{msg}</em>}
        </div>
    );
};

export const FormTextarea = ({ label, name, value, defaultValue, error, onChange, onBlur, placeholder, required, suffix, className = '', inputClass = '', inputRef, rows = 3, labelRight, hints, helperText, ...p }) => {
    const { hasErr, msg } = getErr(error, value);
    return (
        <div className={`input-case ${hasErr ? 'invalid' : ''} ${className}`}>
            {label && (
                <p style={labelRight ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' } : undefined}>
                    <span>{label} <Lbl req={required} sfx={suffix} /></span>
                    {labelRight && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{labelRight}</span>}
                </p>
            )}
            <textarea ref={inputRef} name={name} value={value} defaultValue={defaultValue} onChange={onChange} onBlur={onBlur} placeholder={placeholder} rows={rows} className={inputClass} {...p} required={!!required} />
            {msg && <em>{msg}</em>}
            {(hints || helperText) && <small>{hints || helperText}</small>}
        </div>
    );
};

export const FormToggle = ({ label, name, checked, onChange, description, className = '', switchClass = '', required, suffix, error, ...p }) => {
    const { hasErr, msg } = getErr(error, checked);
    return (
        <div className={`input-case toggle-case ${hasErr ? 'invalid' : ''} ${className}`}>
            <div className="toggle-header">
                <div className={`toggle-switch ${switchClass}`.trim()}>
                    <label>
                        <input type="checkbox" name={name} checked={!!checked} onChange={onChange} required={!!required} {...p} />
                        <span></span>
                    </label>
                </div>
                {label && <p><span>{label} <Lbl req={required} sfx={suffix} /></span></p>}
            </div>
            {description && <small className="toggle-case-desc">{description}</small>}
            {msg && <em>{msg}</em>}
        </div>
    );
};

export const FormSearchableSelect = ({ label, name, value, error, options = [], defaultOptions = [], loading = false, renderOption, onChange, placeholder = 'Search', required, suffix, iconRight, onIconRightClick, iconRightClass = '', className = '', inputRef, ...p }) => {
    const { hasErr, msg } = getErr(error, value);
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(query.toLowerCase())
    );

    const displayOptions = query ? filteredOptions : defaultOptions;

    const handleSelect = (option) => {
        setQuery(option.label);
        setIsOpen(false);
        if (onChange) {
            onChange({ target: { name, value: option.value } });
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (value) {
            const selected = options.find(opt => opt.value === value);
            if (selected) setQuery(selected.label);
            else setQuery(value);
        } else {
            setQuery('');
        }
    }, [value, options]);

    const handleInputChange = (e) => {
        const newVal = e.target.value;
        setQuery(newVal);
        setIsOpen(true);
        if (onChange) onChange({ target: { name, value: newVal } });
    };

    const isDouble = !!iconRight;
    const rightClass = isDouble ? 'right-double' : 'right';

    return (
        <div className={`input-case ${hasErr ? 'invalid' : ''} ${className}`} ref={containerRef}>
            {label && <p>{label} <Lbl req={required} sfx={suffix} /></p>}
            <div className='search-input-case'>
                <div className={`input-group left ${rightClass} ${isOpen ? 'active' : ''} ${hasErr ? 'invalid' : ''}`}>
                    <input ref={inputRef} type='text' name={name} placeholder={placeholder} value={query} onChange={handleInputChange} onFocus={() => setIsOpen(true)} autoComplete="off" {...p} />
                    <Icon icon={icons.search} className='input-icon' />
                    {iconRight && <Icon icon={iconRight} className={`input-icon right ${iconRightClass}`} onClick={onIconRightClick} />}
                    <Icon icon={isOpen ? icons.chevronUp : icons.chevronDown} className='input-icon right' />
                </div>
                {isOpen && (displayOptions.length > 0 || query || loading) && (
                    <ul className='search-input-dropdown scrollbar-small'>
                        {loading ? (
                            <li className='search-input-no-result'>Searching...</li>
                        ) : displayOptions.length > 0 ? (
                            displayOptions.map((opt, i) => (
                                <li key={i} className={`${query === opt.label ? 'selected' : ''}`} onClick={() => handleSelect(opt)} >
                                    {renderOption ? renderOption(opt) : <div className='search-input-item'>{opt.label}</div>}
                                </li>
                            ))
                        ) : (
                            query && <li className='search-input-no-result'>No matches found for "{query}"</li>
                        )}
                    </ul>
                )}
            </div>
            {msg && <em>{msg}</em>}
        </div>
    );
};


export const FormPassword = ({ label, name, value, error, onChange, onBlur, onFocus, placeholder = 'Password', required, suffix, iconLeft = icons.key, className = '', inputRef, minChars = 6, noHints = false, ...p }) => {
    const [show, setShow] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const validations = validationRules.passwordComplexity(value, minChars);

    const isAllValid = Object.values(validations).every(Boolean);
    const { hasErr } = getErr(error, value);
    const displayError = (!noHints && hasErr && !isAllValid) ? 'Password does not meet requirements' : error;

    const handleFocus = (e) => {
        setIsFocused(true);
        if (onFocus) onFocus(e);
    };

    const handleBlur = (e) => {
        setIsFocused(false);
        if (onBlur) onBlur(e);
    };

    return (
        <FormInput
            type={show ? 'text' : 'password'}
            label={label}
            name={name}
            value={value}
            error={displayError}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            required={required}
            suffix={suffix}
            iconLeft={iconLeft}
            iconRight={show ? icons.eyeSlash : icons.eye}
            onIconRightClick={() => setShow(!show)}
            className={className}
            inputRef={inputRef}
            hints={!noHints && (isFocused || hasErr || (value && !isAllValid)) && (
                <ul className="input-hints">
                    <li className={validations.length ? 'valid' : (hasErr ? 'invalid' : '')}>- Minimum {minChars} characters</li>
                    <li className={validations.uppercase ? 'valid' : (hasErr ? 'invalid' : '')}>- At least one uppercase letter</li>
                    <li className={validations.number ? 'valid' : (hasErr ? 'invalid' : '')}>- At least one number</li>
                    <li className={validations.special ? 'valid' : (hasErr ? 'invalid' : '')}>- At least one special character</li>
                </ul>
            )}
            {...p}
        />
    );
};

export const FormDateSelect = ({ label, name, value, error, onChange, required, suffix, className = '', ...p }) => {
    const { hasErr, msg } = getErr(error, value);

    // Internal state to track parts independently of the parent value
    // This prevents dropdowns from resetting when the parent value is '' (incomplete)
    const [parts, setParts] = useState(() => {
        const p = value ? value.split('-') : ['', '', ''];
        return { y: p[0] || '', m: p[1] || '', d: p[2] || '' };
    });

    // Sync internal state if the value prop changes from outside (e.g. form reset)
    useEffect(() => {
        const p = value ? value.split('-') : ['', '', ''];
        setParts({ y: p[0] || '', m: p[1] || '', d: p[2] || '' });
    }, [value]);

    const handlePartChange = (part, newVal) => {
        const newParts = { ...parts, [part]: newVal };
        setParts(newParts);

        // Only send the full date if all parts are present
        // If incomplete, we send '' so that parent 'required' validation fails
        const isComplete = newParts.y && newParts.m && newParts.d;
        const newDate = isComplete ? `${newParts.y}-${newParts.m.padStart(2, '0')}-${newParts.d.padStart(2, '0')}` : '';

        if (onChange) onChange({ target: { name, value: newDate } });
    };

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
    const months = [
        { v: '01', l: 'January' }, { v: '02', l: 'February' }, { v: '03', l: 'March' },
        { v: '04', l: 'April' }, { v: '05', l: 'May' }, { v: '06', l: 'June' },
        { v: '07', l: 'July' }, { v: '08', l: 'August' }, { v: '09', l: 'September' },
        { v: '10', l: 'October' }, { v: '11', l: 'November' }, { v: '12', l: 'December' }
    ];

    const getDaysInMonth = (y, m) => {
        if (!y || !m) return 31;
        return new Date(y, m, 0).getDate();
    };
    const daysCount = getDaysInMonth(parts.y || 2000, parts.m || 1);
    const days = Array.from({ length: daysCount }, (_, i) => i + 1);

    return (
        <div className={`input-case ${hasErr ? 'invalid' : ''} ${className}`}>
            {label && <p>{label} <Lbl req={required} sfx={suffix} /></p>}
            <div className='select-date-group'>
                <select value={parts.m} onChange={(e) => handlePartChange('m', e.target.value)} className={`w-month ${!parts.m ? 'is-placeholder' : ''}`} name={`${name}_month`} autoComplete='off'>
                    <option value=''>Month</option>
                    {months.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
                <select value={parts.d} onChange={(e) => handlePartChange('d', e.target.value)} className={`w-day ${!parts.d ? 'is-placeholder' : ''}`} name={`${name}_day`} autoComplete='off'>
                    <option value=''>Day</option>
                    {days.map(d => <option key={d} value={String(d).padStart(2, '0')}>{d}</option>)}
                </select>
                <select value={parts.y} onChange={(e) => handlePartChange('y', e.target.value)} className={`w-year ${!parts.y ? 'is-placeholder' : ''}`} name={`${name}_year`} autoComplete='off'>
                    <option value=''>Year</option>
                    {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
                </select>
            </div>
            {msg && <em>{msg}</em>}
        </div>
    );
};

const generateThemeShades = (hex) => {
    if (!hex || !/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/i.test(hex)) hex = '#1877f2'; // Fallback

    let r = 0, g = 0, b = 0, a = '';
    if (hex.length === 4 || hex.length === 5) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
        if (hex.length === 5) a = hex[4] + hex[4];
    } else {
        r = parseInt(hex.substring(1, 3), 16);
        g = parseInt(hex.substring(3, 5), 16);
        b = parseInt(hex.substring(5, 7), 16);
        if (hex.length >= 8) a = hex.substring(7, 9);
    }

    const adjust = (val, factor) => {
        let newVal = factor > 0 ? val + (255 - val) * factor : val + val * factor;
        let hexStr = Math.round(newVal).toString(16);
        return hexStr.length === 1 ? '0' + hexStr : hexStr;
    };

    return [
        `#${adjust(r, 0.8)}${adjust(g, 0.8)}${adjust(b, 0.8)}${a}`,
        `#${adjust(r, 0.55)}${adjust(g, 0.55)}${adjust(b, 0.55)}${a}`,
        `#${adjust(r, 0.3)}${adjust(g, 0.3)}${adjust(b, 0.3)}${a}`,
        hex.toLowerCase(),
        `#${adjust(r, -0.25)}${adjust(g, -0.25)}${adjust(b, -0.25)}${a}`,
        `#${adjust(r, -0.45)}${adjust(g, -0.45)}${adjust(b, -0.45)}${a}`,
        `#${adjust(r, -0.65)}${adjust(g, -0.65)}${adjust(b, -0.65)}${a}`
    ];
};

const AdvancedColorPicker = ({ color, onChange }) => {
    const [localHex, setLocalHex] = useState('');
    const [localAlpha, setLocalAlpha] = useState('');
    const [presetColors, setPresetColors] = useState([]);
    const [resolvedColor, setResolvedColor] = useState('#000000');

    useLayoutEffect(() => {
        const updateShades = () => {
            const themeEl = document.querySelector('.cms-webpage') || document.documentElement;
            const rootStyle = getComputedStyle(themeEl);

            const lightest = rootStyle.getPropertyValue('--primary-lightest').trim();
            const lighter = rootStyle.getPropertyValue('--primary-lighter').trim();
            const light = rootStyle.getPropertyValue('--primary-light').trim();
            const base = rootStyle.getPropertyValue('--primary').trim() || rootStyle.getPropertyValue('--primary-color').trim();
            const dark = rootStyle.getPropertyValue('--primary-dark').trim();
            const darker = rootStyle.getPropertyValue('--primary-darker').trim();
            const darkest = rootStyle.getPropertyValue('--primary-darkest').trim();

            const secLightest = rootStyle.getPropertyValue('--secondary-lightest').trim();
            const secLighter = rootStyle.getPropertyValue('--secondary-lighter').trim();
            const secLight = rootStyle.getPropertyValue('--secondary-light').trim();
            const secBase = rootStyle.getPropertyValue('--secondary').trim() || rootStyle.getPropertyValue('--secondary-color').trim();
            const secDark = rootStyle.getPropertyValue('--secondary-dark').trim();
            const secDarker = rootStyle.getPropertyValue('--secondary-darker').trim();
            const secDarkest = rootStyle.getPropertyValue('--secondary-darkest').trim();

            let presetGroups = [];

            if (lightest && lighter && light && base && dark && darker && darkest) {
                presetGroups.push([
                    { var: 'var(--primary-lightest)', hex: lightest, label: 'Primary Lightest' },
                    { var: 'var(--primary-lighter)', hex: lighter, label: 'Primary Lighter' },
                    { var: 'var(--primary-light)', hex: light, label: 'Primary Light' },
                    { var: 'var(--primary-color)', hex: base, label: 'Primary' },
                    { var: 'var(--primary-dark)', hex: dark, label: 'Primary Dark' },
                    { var: 'var(--primary-darker)', hex: darker, label: 'Primary Darker' },
                    { var: 'var(--primary-darkest)', hex: darkest, label: 'Primary Darkest' }
                ]);
            } else {
                const shades = generateThemeShades(base || '#1877f2');
                presetGroups.push(shades.map((hex, i) => ({ hex, label: ['Primary Lightest', 'Primary Lighter', 'Primary Light', 'Primary', 'Primary Dark', 'Primary Darker', 'Primary Darkest'][i] })));
            }

            if (secLightest && secLighter && secLight && secBase && secDark && secDarker && secDarkest) {
                presetGroups.push([
                    { var: 'var(--secondary-lightest)', hex: secLightest, label: 'Secondary Lightest' },
                    { var: 'var(--secondary-lighter)', hex: secLighter, label: 'Secondary Lighter' },
                    { var: 'var(--secondary-light)', hex: secLight, label: 'Secondary Light' },
                    { var: 'var(--secondary)', hex: secBase, label: 'Secondary' },
                    { var: 'var(--secondary-dark)', hex: secDark, label: 'Secondary Dark' },
                    { var: 'var(--secondary-darker)', hex: secDarker, label: 'Secondary Darker' },
                    { var: 'var(--secondary-darkest)', hex: secDarkest, label: 'Secondary Darkest' }
                ]);
            } else if (secBase) {
                const shades = generateThemeShades(secBase);
                presetGroups.push(shades.map((hex, i) => ({ hex, label: ['Secondary Lightest', 'Secondary Lighter', 'Secondary Light', 'Secondary', 'Secondary Dark', 'Secondary Darker', 'Secondary Darkest'][i] })));
            }

            setPresetColors(presetGroups);
        };

        // Initial load
        updateShades();

        // Watch for dynamic changes to the inline style
        const observer = new MutationObserver((mutations) => {
            for (let mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    updateShades();
                }
            }
        });

        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
        observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
        const cmsWebpage = document.querySelector('.cms-webpage');
        if (cmsWebpage) {
            observer.observe(cmsWebpage, { attributes: true, attributeFilter: ['style'] });
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        let currentHex = color || '#000000';

        if (typeof currentHex === 'string' && currentHex.startsWith('var(')) {
            const varName = currentHex.match(/var\(([^)]+)\)/);
            if (varName && varName[1]) {
                const themeEl = document.querySelector('.cms-webpage') || document.documentElement;
                const rootStyle = getComputedStyle(themeEl);
                const resolved = rootStyle.getPropertyValue(varName[1].trim()).trim();
                if (resolved) currentHex = resolved;
            }
        }

        setResolvedColor(currentHex);

        if (currentHex === 'transparent') currentHex = '#00000000';
        if (currentHex.length === 7) currentHex += 'ff';
        if (currentHex.length === 4) {
            currentHex = '#' + currentHex[1] + currentHex[1] + currentHex[2] + currentHex[2] + currentHex[3] + currentHex[3] + 'ff';
        }

        const base = currentHex.substring(0, 7);
        const alphaHex = currentHex.length === 9 ? currentHex.substring(7, 9) : 'ff';
        const alphaPercent = Math.round((parseInt(alphaHex, 16) / 255) * 100);

        setLocalHex(base.replace('#', ''));
        setLocalAlpha(alphaPercent.toString());
    }, [color]);

    const handleColorChange = (newColor) => {
        let currentHex = newColor;

        if (typeof currentHex === 'string' && currentHex.startsWith('var(')) {
            const varName = currentHex.match(/var\(([^)]+)\)/);
            if (varName && varName[1]) {
                const themeEl = document.querySelector('.cms-webpage') || document.documentElement;
                const rootStyle = getComputedStyle(themeEl);
                const resolved = rootStyle.getPropertyValue(varName[1].trim()).trim();
                if (resolved) currentHex = resolved;
            }
        }

        setResolvedColor(currentHex);

        if (currentHex === 'transparent') currentHex = '#00000000';
        if (currentHex.length === 7) currentHex += 'ff';
        if (currentHex.length === 4) {
            currentHex = '#' + currentHex[1] + currentHex[1] + currentHex[2] + currentHex[2] + currentHex[3] + currentHex[3] + 'ff';
        }

        const base = currentHex.substring(0, 7);
        const alphaHex = currentHex.length === 9 ? currentHex.substring(7, 9) : 'ff';
        const alphaPercent = Math.round((parseInt(alphaHex, 16) / 255) * 100);

        setLocalHex(base.replace('#', ''));
        setLocalAlpha(alphaPercent.toString());

        if (onChange) onChange(newColor);
    };

    const handleHexInputChange = (e) => {
        let val = e.target.value.replace(/[^0-9A-Fa-f]/g, '').substring(0, 6);
        setLocalHex(val);
        const alphaHex = Math.round((parseInt(localAlpha || '100', 10) / 100) * 255).toString(16).padStart(2, '0');
        const colorStr = alphaHex.toLowerCase() === 'ff' ? `#${val}` : `#${val}${alphaHex}`;
        if (val.length === 3 || val.length === 6) {
            setResolvedColor(colorStr);
        }
        if (onChange) onChange(colorStr);
    };

    const handleAlphaInputChange = (e) => {
        let val = e.target.value.replace(/[^0-9]/g, '');
        if (val !== '' && parseInt(val, 10) > 100) val = '100';
        setLocalAlpha(val);
        if (val !== '') {
            const alphaHex = Math.round((parseInt(val, 10) / 100) * 255).toString(16).padStart(2, '0');
            const baseHex = localHex.length === 6 ? localHex : '000000';
            const colorStr = alphaHex.toLowerCase() === 'ff' ? `#${baseHex}` : `#${baseHex}${alphaHex}`;
            if (onChange) onChange(colorStr);
        }
    };

    const resetColor = () => {
        setLocalHex('000000');
        setLocalAlpha('100');
        if (onChange) onChange('');
    };

    return (
        <div className="advanced-color-picker">
            <HexAlphaColorPicker
                color={(typeof resolvedColor === 'string' && /^#[0-9A-Fa-f]{6,8}$/i.test(resolvedColor)) ? resolvedColor : '#000000'}
                onChange={handleColorChange}
            />
            <div className="advanced-color-controls">
                <div className="adv-color-input-wrap hex">
                    <span>#</span>
                    <input name='advColorHex' type="text" value={localHex} onChange={handleHexInputChange} autoComplete='off' />
                </div>
                <div className="adv-color-input-wrap alpha">
                    <input type="text" name='advColorAlpha' value={localAlpha} onChange={handleAlphaInputChange} autoComplete='off' />
                    <span>%</span>
                </div>
                <button type="button" className="adv-color-reset" onClick={resetColor} title="Reset Color">
                    <Icon icon={icons.close} />
                    {/* <Icon icon={icons.refresh || icons.rotateCcw || 'fas fa-sync'} /> */}
                </button>
            </div>
            {presetColors.length > 0 && (
                <div className="adv-color-presets-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '8px', marginTop: '10px', borderTop: '1px solid var(--popup-border-color)' }}>
                    {presetColors.map((group, groupIndex) => (
                        <div key={groupIndex} className="adv-color-presets" style={{ paddingTop: 0, marginTop: 0, borderTop: 'none' }}>
                            {group.map((preset, index) => (
                                <button
                                    key={`${preset.hex}-${index}`}
                                    type="button"
                                    className="adv-color-preset-circle"
                                    style={{ backgroundColor: preset.hex }}
                                    onClick={() => handleColorChange(preset.var || preset.hex)}
                                    title={preset.label}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const getPresetDisplayLabel = (val) => {
    if (!val || typeof val !== 'string' || !val.startsWith('var(')) return val;
    const map = {
        'var(--primary-lightest)': 'P-L3',
        'var(--primary-lighter)': 'P-L2',
        'var(--primary-light)': 'P-L1',
        'var(--primary)': 'Pri',
        'var(--primary-color)': 'Pri',
        'var(--primary-dark)': 'P-D1',
        'var(--primary-darker)': 'P-D2',
        'var(--primary-darkest)': 'P-D3',
        'var(--secondary-lightest)': 'S-L3',
        'var(--secondary-lighter)': 'S-L2',
        'var(--secondary-light)': 'S-L1',
        'var(--secondary)': 'Sec',
        'var(--secondary-color)': 'Sec',
        'var(--secondary-dark)': 'S-D1',
        'var(--secondary-darker)': 'S-D2',
        'var(--secondary-darkest)': 'S-D3'
    };
    return map[val] || val;
};

export const useResolvedColor = (color) => {
    const [resolvedColor, setResolvedColor] = useState('transparent');

    const updateResolvedColor = useCallback(() => {
        if (typeof color !== 'string') {
            setResolvedColor('transparent');
            return;
        }
        if (color.startsWith('var(')) {
            const varMatch = color.match(/var\(([^)]+)\)/);
            if (varMatch && varMatch[1]) {
                const themeEl = document.querySelector('.cms-webpage') || document.documentElement;
                const rootStyle = getComputedStyle(themeEl);
                const resolved = rootStyle.getPropertyValue(varMatch[1].trim()).trim();
                setResolvedColor(resolved || color);
                return;
            }
        }
        if (color.startsWith('#') || color.startsWith('rgb') || color.startsWith('hsl')) {
            setResolvedColor(color);
            return;
        }
        setResolvedColor('transparent');
    }, [color]);

    useEffect(() => {
        updateResolvedColor();
    }, [updateResolvedColor]);

    useEffect(() => {
        if (!color || typeof color !== 'string' || !color.startsWith('var(')) return;

        const observer = new MutationObserver((mutations) => {
            for (let mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    updateResolvedColor();
                }
            }
        });

        const cmsWebpage = document.querySelector('.cms-webpage');
        if (cmsWebpage) {
            observer.observe(cmsWebpage, { attributes: true, attributeFilter: ['style'] });
        } else {
            observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
            observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
        }

        return () => observer.disconnect();
    }, [color, updateResolvedColor]);

    return resolvedColor;
};

export const FormColorPicker = ({ label, name, value, onChange, error, className = '', disabled = false, defaultValue = '' }) => {
    const resolvedColor = useResolvedColor(value);
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef(null);

    const handleClear = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        if (onChange) {
            onChange({ target: { name, value: defaultValue } });
        }
    };

    const handleClick = () => {
        if (disabled) return;
        setIsOpen(true);
    };

    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);

    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers: [
            { name: 'offset', options: { offset: [0, 8] } },
            { name: 'preventOverflow', options: { padding: 10 } },
        ],
    });

    useEffect(() => {
        if (isOpen && update) {
            update();
        }
    }, [isOpen, update, value]);

    useEffect(() => {
        if (!popperElement || !update) return;
        const observer = new ResizeObserver(() => {
            update();
        });
        observer.observe(popperElement);
        return () => observer.disconnect();
    }, [popperElement, update]);

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popperElement && popperElement.contains(event.target)) {
                return;
            }
            if (referenceElement && referenceElement.contains(event.target)) {
                return;
            }
            setIsOpen(false);
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, popperElement, referenceElement]);

    return (
        <div className={`input-case ${error ? 'invalid' : ''} ${className}`}>
            {label && <p>{label}</p>}

            <div className={`custom-color-picker ${disabled ? 'disabled' : ''}`} ref={setReferenceElement} >
                <div className='custom-color-picker-trigger' onClick={handleClick} >
                    <div className="color-square" style={{ backgroundColor: resolvedColor || 'transparent' }}></div>
                    <input
                        name='customColor_value'
                        type="text"
                        className="color-hex-text"
                        value={getPresetDisplayLabel(value) || ''}
                        placeholder="No color"
                        onChange={(e) => {
                            if (onChange) onChange({ target: { name, value: e.target.value } });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={disabled}
                        autoComplete='off'
                    />
                </div>
                {value && !disabled && (
                    <button type="button" className="color-clear-btn" onClick={handleClear}>
                        <Icon icon={icons.close} />
                    </button>
                )}

                {isOpen && !disabled && createPortal(
                    <div className="custom-color-popover ignore-outside-click" ref={setPopperElement} style={styles.popper} {...attributes.popper}>
                        <AdvancedColorPicker color={(typeof value === 'string' && value) ? value : '#000000'} onChange={(color) => { if (onChange) onChange({ target: { name, value: color } }); }} />
                    </div>,
                    document.body
                )}
            </div>
            {error && <em>{error}</em>}
        </div>
    );
};

export const PopoverColorPicker = ({ color, onChange, disabled, children }) => {
    const resolvedColor = useResolvedColor(color);
    const [isOpen, setIsOpen] = useState(false);
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);

    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers: [
            { name: 'offset', options: { offset: [0, 8] } },
            { name: 'preventOverflow', options: { padding: 10 } },
        ],
    });

    useEffect(() => {
        if (isOpen && update) {
            update();
        }
    }, [isOpen, update, color]);

    useEffect(() => {
        if (!popperElement || !update) return;
        const observer = new ResizeObserver(() => {
            update();
        });
        observer.observe(popperElement);
        return () => observer.disconnect();
    }, [popperElement, update]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popperElement && popperElement.contains(event.target)) {
                return;
            }
            if (referenceElement && referenceElement.contains(event.target)) {
                return;
            }
            setIsOpen(false);
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, popperElement, referenceElement]);

    return (
        <div className="popover-color-picker-wrap" ref={setReferenceElement}>
            {children ? (
                <div
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (!disabled) setIsOpen(!isOpen);
                    }}
                    className={`popover-color-picker-child ${disabled ? 'disabled' : ''}`}
                >
                    {typeof children === 'function' ? children(resolvedColor) : children}
                </div>
            ) : (
                <div
                    className={`popover-color-picker-box ${disabled ? 'disabled' : ''}`}
                    style={{
                        backgroundColor: resolvedColor
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (!disabled) setIsOpen(!isOpen);
                    }}
                />
            )}
            {isOpen && !disabled && createPortal(
                <div
                    className="custom-color-popover ignore-outside-click"
                    ref={setPopperElement}
                    style={styles.popper}
                    {...attributes.popper}
                    onMouseDown={(e) => {
                        if (e.target.tagName !== 'INPUT') {
                            e.preventDefault();
                        }
                    }}
                >
                    <AdvancedColorPicker
                        color={(typeof color === 'string' && color) ? color : '#000000'}
                        onChange={(c) => { if (onChange) onChange(c); }}
                    />
                </div>,
                document.body
            )}
        </div>
    );
};

// Inner sortable item for FormMediaGallery
const SortableMediaItem = ({ id, file, onDelete, baseUrl }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} className={`form-media-item ${file.isStaged ? 'staged' : ''}`}>
            <div className="form-media-item-content" {...attributes} {...listeners} style={{ cursor: 'grab', width: '100%', height: '100%' }}>
                {file.type === 'photo' ? (
                    <img src={file.isStaged ? file.url : `${baseUrl}${file.url}`} alt={file.name} draggable={false} />
                ) : (
                    <>
                        <video src={`${file.isStaged ? file.url : `${baseUrl}${file.url}`}#t=0.1`} preload="metadata" draggable={false} />
                        <div className='form-media-video-icon' style={{ pointerEvents: 'none' }}><Icon icon={icons.video} /></div>
                    </>
                )}
                {file.isStaged && <div className="staged-badge">New</div>}
            </div>
            <div className='form-media-item-nav'>
                <button type='button' className='btn-danger' onPointerDown={(e) => { e.stopPropagation(); onDelete(file); }} title="Remove"><Icon icon={icons.times} /></button>
            </div>
        </div>
    );
};

export const FormMediaGallery = forwardRef(({ label, initialMedia = [], isSubmitting = false, baseUrl = '', onMediaChange }, ref) => {
    const [mediaItems, setMediaItems] = useState([]);
    const [mediaToDelete, setMediaToDelete] = useState(new Set());
    const fileInputRef = useRef(null);

    // Initialize state when initialMedia changes
    useEffect(() => {
        setMediaItems(initialMedia.map(f => ({ ...f, id: f.name })));
    }, [initialMedia]);

    useImperativeHandle(ref, () => ({
        getUploadData: () => {
            const staged = mediaItems.filter(f => f.isStaged);
            const uploadData = new FormData();
            staged.forEach(s => uploadData.append('files', s.file));
            return { hasStaged: staged.length > 0, uploadData, count: staged.length };
        },
        getMediaToDelete: () => Array.from(mediaToDelete),
        getFinalOrder: () => mediaItems.map(f => f.name)
    }));

    useEffect(() => {
        if (onMediaChange) onMediaChange({ items: mediaItems, toDelete: mediaToDelete });
    }, [mediaItems, mediaToDelete, onMediaChange]);

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        const newStaged = files.map(file => ({
            id: file.name,
            file,
            name: file.name,
            url: URL.createObjectURL(file),
            type: file.type.startsWith('video') ? 'video' : 'photo',
            isStaged: true
        }));
        setMediaItems(prev => [...prev, ...newStaged]);
        e.target.value = '';
    };

    const handleDeleteMedia = (file) => {
        if (file.isStaged) {
            URL.revokeObjectURL(file.url);
        } else {
            setMediaToDelete(prev => new Set(prev).add(file.name));
        }
        setMediaItems(prev => prev.filter(f => f.id !== file.id));
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            setMediaItems((items) => {
                const oldIndex = items.findIndex(item => item.id === active.id);
                const newIndex = items.findIndex(item => item.id === over.id);
                const newItems = [...items];
                const [movedItem] = newItems.splice(oldIndex, 1);
                newItems.splice(newIndex, 0, movedItem);
                return newItems;
            });
        }
    };

    return (
        <div className='input-case'>
            {label && <p>{label}</p>}
            <div className='form-media-case'>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={mediaItems.map(m => m.id)} strategy={rectSortingStrategy}>
                        <div className='form-media-grid'>
                            {mediaItems.map((file) => (
                                <SortableMediaItem key={file.id} id={file.id} file={file} onDelete={handleDeleteMedia} baseUrl={baseUrl} />
                            ))}
                            <div className={`form-media-upload ${isSubmitting ? 'disabled' : ''}`} onClick={() => !isSubmitting && fileInputRef.current?.click()}>
                                <Icon icon={icons.addSimple} />
                                <p>{isSubmitting ? 'Processing...' : 'Add Media'}</p>
                                <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple onChange={handleFileUpload} accept="image/*,video/*" disabled={isSubmitting} />
                            </div>
                        </div>
                    </SortableContext>
                </DndContext>
            </div>
        </div>
    );
});
